import {
	EmptyStateVariant,
	ErrorState,
	Field,
	ListSkeleton,
	SelectField,
	type TableColumn,
	type TableSort,
} from '@today/ui-components'
import { Badge, Button, useDebounce } from '@today/ui-primitives'
import { DataTable } from '@today/ui-recipes'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import {
	type CustomerFilters,
	type CustomerRowView,
	CustomerSortKey,
	type Option,
	type Translate,
} from './customer-types'

/** UX §5: a text filter applies once it has been unchanged this long (TO-697 REQ-003). */
const TEXT_FILTER_DEBOUNCE_MS = 250

/**
 * Props for {@link CustomerListView}.
 *
 * @property rows - The current page, already mapped for display (masked values as returned).
 * @property filters - Controlled filter values (mirrored in the URL by the page hook).
 * @property total - Total customers across all pages, for the total line.
 * @property loading - True while the first request of the current query is in flight with no rows held.
 * @property sort - The current server sort, or undefined for the server default.
 * @property error - True when the last request failed: renders the retry state.
 * @property searchTooShort - True while the search holds 1 character (no request sent).
 * @property onClearFilters - Removes every filter (wired to the filtered-empty state).
 * @property onCreate - Present only when the viewer may create customers.
 * @property onStartOffer - Present only when the viewer may write offers; adds the row action.
 */
export interface CustomerListViewProps {
	t: Translate
	rows: CustomerRowView[]
	filters: CustomerFilters
	orgUnits: Option[]
	page: number
	pageCount: number
	total: number
	loading: boolean
	sort?: TableSort
	error: boolean
	searchTooShort: boolean
	onFilterChange: (key: keyof CustomerFilters, value: string) => void
	onPageChange: (page: number) => void
	onSortChange: (sort: TableSort) => void
	onClearFilters: () => void
	onOpen: (id: string) => void
	onRetry: () => void
	onCreate?: () => void
	onStartOffer?: (id: string) => void
}

const FILTER_KEYS: Array<keyof CustomerFilters> = ['type', 'status', 'orgUnitId', 'tags', 'search']

const hasActiveFilter = (filters: CustomerFilters): boolean => FILTER_KEYS.some((key) => filters[key] !== '')

const columns = (
	t: Translate,
	onOpen: (id: string) => void,
	onStartOffer: ((id: string) => void) | undefined,
): TableColumn<CustomerRowView>[] => {
	const dataColumns: TableColumn<CustomerRowView>[] = [
		{
			key: CustomerSortKey.FULL_NAME,
			header: t('list.columns.name'),
			sortable: true,
			cell: (row) => (
				<Button variant="muted" size="sm" onPress={() => onOpen(row.id)}>
					{row.fullName}
				</Button>
			),
		},
		{
			key: 'type',
			header: t('list.columns.type'),
			cell: (row) => <Badge variant={row.type === 'customer' ? 'primary' : 'muted'}>{t(`type.${row.type}`)}</Badge>,
		},
		{ key: 'status', header: t('list.columns.status'), cell: (row) => t(`status.${row.status}`) },
		{ key: 'primaryEmail', header: t('list.columns.email'), cell: (row) => row.primaryEmail ?? '—' },
		{ key: 'primaryMobile', header: t('list.columns.mobile'), cell: (row) => row.primaryMobile ?? '—' },
		{ key: 'tags', header: t('list.columns.tags'), cell: (row) => row.tags.join(', ') },
		{ key: 'orgUnitName', header: t('list.columns.orgUnit') },
		{ key: 'assignedUserName', header: t('list.columns.assignedUser'), cell: (row) => row.assignedUserName ?? '—' },
		{ key: CustomerSortKey.CREATED_AT, header: t('list.columns.createdAt'), sortable: true },
	]
	if (!onStartOffer) return dataColumns
	// The column exists only for viewers who may write offers (REQ-005): absent, not disabled.
	const actionColumn: TableColumn<CustomerRowView> = {
		key: 'actions',
		header: t('list.columns.actions'),
		cell: (row) => (
			<Button variant="secondary" size="sm" onPress={() => onStartOffer(row.id)}>
				{t('list.startOffer')}
			</Button>
		),
	}
	return [...dataColumns, actionColumn]
}

interface DebouncedTextFieldProps {
	label: string
	value: string
	description?: string
	onSettle: (value: string) => void
}

/**
 * A text filter that reports its value once it has been unchanged for 250 ms (REQ-003), and
 * follows the controlled `value` when it changes from outside (browser Back, Clear filters).
 */
function DebouncedTextField({ label, value, description, onSettle }: DebouncedTextFieldProps): ReactElement {
	const [draft, setDraft] = useState(value)
	const settled = useDebounce(draft, TEXT_FILTER_DEBOUNCE_MS)
	// The last value this field and its parent agreed on: an outside change and our own report
	// both land here, so neither echoes the other back.
	const agreed = useRef(value)
	// The parent passes a fresh callback each render; reading it through a ref keeps the report
	// effect keyed on `settled` alone, so a re-render never re-reports a stale settled value.
	const onSettleRef = useRef(onSettle)
	onSettleRef.current = onSettle

	useEffect(() => {
		if (value === agreed.current) return
		agreed.current = value
		setDraft(value)
	}, [value])

	useEffect(() => {
		if (settled === agreed.current) return
		agreed.current = settled
		onSettleRef.current(settled)
	}, [settled])

	return <Field label={label} value={draft} description={description} onChange={(e) => setDraft(e.target.value)} />
}

function ListFilters({ t, filters, orgUnits, searchTooShort, onFilterChange }: CustomerListViewProps) {
	const choice = (key: 'type' | 'status', values: string[]) => (
		<SelectField
			label={t(`filters.${key}`)}
			value={filters[key]}
			onValueChange={(value) => onFilterChange(key, value)}
			options={[{ id: '', text: t('filters.all') }, ...values.map((v) => ({ id: v, text: t(`${key}.${v}`) }))]}
		/>
	)
	return (
		<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
			{choice('type', ['lead', 'customer'])}
			{choice('status', ['active', 'inactive'])}
			<SelectField
				label={t('filters.orgUnit')}
				value={filters.orgUnitId}
				onValueChange={(value) => onFilterChange('orgUnitId', value)}
				options={[{ id: '', text: t('filters.all') }, ...orgUnits.map((o) => ({ id: o.id, text: o.label }))]}
			/>
			<DebouncedTextField
				label={t('filters.tags')}
				value={filters.tags}
				onSettle={(value) => onFilterChange('tags', value)}
			/>
			<DebouncedTextField
				label={t('filters.search')}
				value={filters.search}
				description={searchTooShort ? t('list.searchHint') : undefined}
				onSettle={(value) => onFilterChange('search', value)}
			/>
		</div>
	)
}

/** The table area: error, skeleton, or the grid with its no-data / filtered-empty body (REQ-007). */
function ListBody(props: CustomerListViewProps): ReactElement | null {
	const { t } = props
	if (props.error) {
		return <ErrorState message={t('list.error')} labels={{ retry: t('list.retry') }} onRetry={props.onRetry} />
	}
	if (props.loading) return <ListSkeleton labels={{ loading: t('list.loading') }} />
	// A 1-character search sent no request, so "no rows" here says nothing about the data.
	if (props.searchTooShort && props.rows.length === 0) return null

	const filtered = hasActiveFilter(props.filters)
	return (
		// Narrow viewports scroll the table sideways (the UI spec's MVP fallback, no card layout).
		<div className="overflow-x-auto">
			<DataTable
				aria-label={t('list.title')}
				columns={columns(t, props.onOpen, props.onStartOffer)}
				rows={props.rows}
				getRowId={(row) => row.id}
				sort={props.sort}
				onSortChange={props.onSortChange}
				page={props.page}
				pageCount={props.pageCount}
				total={props.total}
				onPageChange={props.onPageChange}
				labels={{ total: (count) => t('list.total', { count }) }}
				emptyStateVariant={filtered ? EmptyStateVariant.FILTERED_ZERO : EmptyStateVariant.NO_DATA}
				emptyStateLabels={{
					noDataTitle: t('list.empty'),
					noDataDescription: t('list.emptyHint'),
					filteredZeroTitle: t('list.noResults'),
					filteredZeroDescription: t('list.noResultsHint'),
					clearFilters: t('list.clearFilters'),
				}}
				onClearFilters={filtered ? props.onClearFilters : undefined}
			/>
		</div>
	)
}

/** Customer list: filters, the sortable data grid with its total and pagination, and its states (organism). */
export function CustomerListView(props: CustomerListViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-4">
			<header className="flex items-center justify-between">
				<h1 className="text-ds-lg font-semibold text-ds-text">{t('list.title')}</h1>
				{props.onCreate ? <Button onPress={props.onCreate}>{t('list.new')}</Button> : null}
			</header>
			<ListFilters {...props} />
			<ListBody {...props} />
		</section>
	)
}
